import { MascotTip } from "@/components/Mascot";
import { OrderHelp } from "@/components/SupportEmail";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Coins, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useGame } from "@/context/GameContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useGame();
  const [st, setSt] = useState({ state: "checking" });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const sid = params.get("session_id");
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { await refresh(); return setSt({ state: "paid", ...data }); }
        if (data.status === "expired") return setSt({ state: "failed" });
      } catch (err) {
        console.warn("Payment status check failed, retrying", err?.response?.status || err?.message);
      }
      if (++tries < 15) setTimeout(poll, 2000); else setSt({ state: "timeout" });
    };
    poll();
  }, [params, refresh]);

  return (
    <main className="min-h-[70vh] grid place-items-center px-6" data-testid="payment-success-page">
      <div className="text-center max-w-md">
        {st.state === "checking" && <><Loader2 className="w-10 h-10 mx-auto animate-spin text-fuchsia-400 mb-6" /><p className="font-display text-2xl">Confirming your payment…</p></>}
        {st.state === "paid" && (
          <>
            <Coins className="w-14 h-14 mx-auto text-amber-300 mb-6" />
            <h1 className="font-display text-4xl text-amber-100 mb-3" data-testid="payment-paid-title">+{st.credits} credits</h1>
            {st.kind && st.kind !== "credits" && st.wallet_address ? (
              <p className="text-slate-300 mb-8" data-testid="payment-pup-note">
                Thanks for buying {st.quantity} {st.label || "Project PUP"}{st.quantity > 1 ? "s" : ""}! {st.quantity > 1 ? "They'll" : "It'll"} arrive at <span className="font-mono-game text-xs">{st.wallet_address}</span> within 48 hours. Your bonus credits are already in your account.
              </p>
            ) : <p className="text-slate-400 mb-8">They're in your account and ready to spend.</p>}
          </>
        )}
        {(st.state === "failed" || st.state === "timeout") && (
          <><p className="font-display text-2xl mb-3">{st.state === "failed" ? "Payment didn't go through" : "Still processing"}</p>
            <p className="text-slate-400 mb-8">{st.state === "timeout" ? "Your credits will appear shortly. Check back in a minute." : "No charge was made."}</p></>
        )}
        <div className="flex gap-3 justify-center">
          <Link to="/journey" data-testid="payment-back-journey" className="ember-btn px-6 py-3 rounded-full text-sm">BACK TO THE HUNT</Link>
          <Link to="/store" data-testid="payment-back-store" className="px-6 py-3 rounded-full text-sm border border-slate-600">Store</Link>
        </div>
        {st.state === "paid" && <MascotTip testid="payment-success-mascot-tip" className="mt-8 justify-center text-left">Nice one, legend! Your credits are already in your den. Go spend them well.</MascotTip>}
        {(st.state === "failed" || st.state === "timeout") && <MascotTip testid="payment-pending-mascot-tip" className="mt-8 justify-center text-left">{st.state === "failed" ? "That one didn't go through and nothing was charged. Give it another go when you're ready." : "Stripe is still finishing up. Your credits will land in a minute or two."}</MascotTip>}
        <div className="mt-8"><OrderHelp testid="payment-success-help" /></div>
      </div>
    </main>
  );
}

export function PaymentCancel() {
  return (
    <main className="min-h-[70vh] grid place-items-center px-6 text-center" data-testid="payment-cancel-page">
      <div>
        <XCircle className="w-12 h-12 mx-auto text-slate-500 mb-6" />
        <h1 className="font-display text-3xl mb-3">Checkout cancelled</h1>
        <p className="text-slate-400 mb-8">No charge was made.</p>
        <Link to="/store" data-testid="payment-cancel-back" className="ember-btn px-6 py-3 rounded-full text-sm">BACK TO STORE</Link>
        <MascotTip testid="payment-cancel-mascot-tip" className="mt-8 justify-center text-left">No worries, nothing was charged. The store's open whenever you are.</MascotTip>
        <div className="mt-8"><OrderHelp testid="payment-cancel-help" /></div>
      </div>
    </main>
  );
}
